import { Button, Stack } from '@solvhealth/jigsaw-v1';
import moment from 'moment';
import React, { useMemo } from 'react';
import { TFuncKey, useTranslation } from 'react-i18next';
import Slot from '@solvhealth/types/interfaces/Location/Slot';
import { useLocationSlots } from './hooks/useSlots';
import history from '~/core/history';
import { analyticsTrackEvent } from '~/core/analytics';
import { ACCOUNT_HOME_CLICK_BOOK_AGAIN_SUGGESTION } from '~/core/analytics/events';
import { LocationWithCareTypes } from '~/components/CareTeam/hooks/useCareTeam';
import { removeSlotsBeforeDisableReservationsUntilTimestamp } from '~/core/location/slots';

type Props = {
  location: LocationWithCareTypes;
};

/**
 * Fetches and displays the next available time slot for a given location.
 * As of 23/4/4 dates beyond tomorrow are currently not returned by useLocationSlots.
 */
const NextAvailableBeyondSameDay = ({ location }: Props) => {
  const { data, status, isLoading } = useLocationSlots(location.id);

  let slots = removeSlotsBeforeDisableReservationsUntilTimestamp(data?.data.slots, location);
  slots = slots
    ?.filter((slot: Slot) => slot.availability > 0 && !slot.isReservationsDisabled)
    .sort((a: Slot, b: Slot) => +new Date(a.appointmentDate) - +new Date(b.appointmentDate));

  const nextSlot =
    status === 'success' && slots?.[0]?.appointmentDate && new Date(slots?.[0]?.appointmentDate);

  const isTomorrow = nextSlot && moment(nextSlot).isSame(moment(new Date()).add(1, 'day'), 'day');

  const { t } = useTranslation(['account']);

  const availabilityKey = useMemo<TFuncKey<['account']>>(() => {
    if (isTomorrow) return 'account:availability.tomorrow';
    return 'account:availability.future';
  }, [isTomorrow]);

  const handleClick = () => {
    if (location.urlSlug) {
      history.push(`${location.urlSlug}?target=booking`, { from: 'account' });
      analyticsTrackEvent(ACCOUNT_HOME_CLICK_BOOK_AGAIN_SUGGESTION, {
        location: location.id,
        lastBooked: location.lastBooked,
      });
    }
  };

  return (
    <Stack alignItems="center" justifyContent="center" row>
      {data && (
        <>
          {nextSlot ? (
            <Button onClick={handleClick} variant="outlined" width="100%">
              {t(availabilityKey, { date: nextSlot })}
            </Button>
          ) : (
            <Button
              disabled
              style={{ border: '1px solid', paddingLeft: '4px', paddingRight: '4px' }}
              variant="outlined"
              width="100%"
            >
              {t('account:availability.noAvailability')}
            </Button>
          )}
        </>
      )}
    </Stack>
  );
};

export default NextAvailableBeyondSameDay;
